/**
 * LcoeChartSection — Estimated Levelized Cost of Energy.
 *
 * Renders a vertical recharts BarChart instead of the prior hand-
 * rolled <div> bar list. Each technology row is a stacked bar of
 * `[spacer, range]` so the visible bar represents the low–high
 * cost range (the spacer is transparent and only positions the
 * range bar's left edge at `low`).
 *
 * Site-specific adjustments (solar GHI, wind capacity factor,
 * seismic SDC, grid retail rate) keep the same math the legacy
 * component used. The grid retail rate becomes a ReferenceLine
 * rather than a row in the bar list — that's the read most readers
 * scan for ("does the BTM tech beat grid?"), and rendering it as a
 * dashed reference makes the cross-over instantly visible.
 *
 * Per-tech adjustment captions render BELOW the chart as a small
 * subtitle list. Putting them on the bars themselves (which the
 * legacy version did) collapsed the readable text under PDF
 * rasterisation.
 */
import React from 'react';
import {
  BarChart, Bar, XAxis, YAxis, CartesianGrid, Tooltip, ReferenceLine, Cell,
} from 'recharts';
import { ChartFrame } from '../../ui/ChartFrame';
import { resolveReportChartPalette } from '../../../lib/reportChartTheme';
import { REPORT_SECTION_H2_CLASS, reportSectionHeadingStyle } from '../../../lib/reportSectionHeading';
import { reportSectionGapClass } from '../../../lib/reportSpacingTokens';
import { EditorialSectionShell } from './EditorialSectionShell';
import { GENERIC_SECTION_INSUFFICIENT_MESSAGE } from '../../../lib/sectionFallbacks/derivedFallbacks';
import { LcoeProvenanceFootnote } from './LcoeProvenanceFootnote';
import { ReportSourceNote } from './ReportSourceNote';
import { SiteRagReport } from '../../../types/siteRag';
import { ReportTheme } from '../../../types/reportTheme';
import { formatApproxLcoePerMwh, formatApproxLcoeRange } from '../../../lib/lcoeDisplay';
import { normalizeElectricityPriceToCents, formatElectricityPriceReference } from '../../../lib/reportFormatters';
import {
  assessLcoePathwayComparability,
  canCompareRowToGrid,
  rowHasCompleteBasis,
  composeLcoeExplainer,
  composeLcoeMethodologyNote,
  LCOE_REFERENCE_VERIFICATION_NOTE,
  composePublishedLcoeReferenceContext,
  describeLcoeDollarBasis,
  deriveLcoeExplainerRows,
  formatPublishedReferenceNoteForBucket,
  isLcoeUnavailableInFrozenSource,
} from '../../../lib/lcoe/lcoeExhibitText';
import {
  resolveReportCorrectedLcoe,
  correctedLcoeForBucket,
  type LcoeTechBucket,
} from '../../../lib/finDecision/reportLcoeSurface';
import { roleColor } from '../../../lib/reportRoleColors';
import {
  CHART_FRAME_BOUNDS, CHART_PLOT_MARGIN, CHART_INK,
} from '../../../lib/reportChartLayoutTokens';
import {
  PathwayComparisonReportSection,
  useStudioPreview,
} from '../sections/PathwayComparison/PathwayComparisonReportSection';

interface LcoeChartSectionProps {
  report: SiteRagReport;
  theme: ReportTheme;
  isVisible: (sectionId: string) => boolean;
  /**
   * TSP-1 — optional CSS-property bag from the parent renderer
   * (SiteRagReportView). Carries `order` (Studio drag-reorder) and
   * `display:none` (Studio eye toggle) so this section participates in
   * the same propagation surface as every other extracted section.
   * Optional for back-compat with legacy callers that didn't pass it.
   */
  sectionStyle?: (sectionId: string) => React.CSSProperties;
  /** Hero / full-width cover — drop the confidential left rail. */
  omitSectionSidebar?: boolean;
  /** When false, defer padding to a padded page wrapper. */
  shellPadding?: boolean;
  sectionPageColumnCount?: (sectionId: string) => 1 | 2;
  /** N1.5 — VITE_EDITORIAL_LAYOUTS on: render the editorial hero-numeral opener. */
  editorialActive?: boolean;
  /** N1.5 — 1-based TOC display number for the hero numeral (NaN ⇒ none). */
  editorialSectionNumber?: number;
}

interface LcoeRow {
  tech: string;
  low: number;
  high: number;
  mid: number | null;
  /** Spacer bar (transparent) — pushes the range bar's left edge to `low` when `low < high`, or 0 for a single-scenario bar. */
  spacer: number;
  /** Visible portion of the stacked bar: `high - low` for a multi-scenario range, or `high` for a single evidenced figure. */
  range: number;
  /** Caption rendered under the chart (corrected source / primary-source comparison reference when pending). */
  note: string;
  /** Index into theme.chartPalette. */
  paletteIdx: number;
  /** No site-specific LCOE resolved → render no bar, show "pending" (never an unverified midpoint or ±8% band). */
  pending: boolean;
  industryNorm: boolean;
}

/**
 * Display string for a row's LCOE value:
 *   - pending (no verified site-specific LCOE) → "pending"
 *   - single evidenced project scenario        → "≈$X/MWh"
 *   - multi-scenario evidenced project range   → "≈$low–$high/MWh"
 */
function lcoeValueLabel(r: LcoeRow): string {
  if (r.pending) return 'pending';
  if (r.low < r.high) return formatApproxLcoeRange(r.low, r.high);
  return formatApproxLcoePerMwh(r.mid ?? r.high);
}

const LcoeChartSection = React.memo<LcoeChartSectionProps>(({ report, theme, isVisible, sectionStyle, omitSectionSidebar = false, shellPadding = true, sectionPageColumnCount, editorialActive = false, editorialSectionNumber }) => {
  // COMBINES (fold #4) — the pinned pathway comparison is FOLDED INTO this
  // section for FEASIBILITY. The pathway data (report.pathwayComparisonAtGen)
  // is unchanged; PathwayComparisonReportSection self-omits when no comparison
  // with 2+ alternatives was pinned (matching the bare-heading guard), so the
  // sub-head only renders when there is real pathway content to show. Studio
  // preview keeps the placeholder so a template author can see + place it.
  const studioPreview = useStudioPreview();
  const pathwaySnapshot = report.pathwayComparisonAtGen;
  const hasPathwayFold =
    report.reportType === 'feasibility'
    && (studioPreview || Boolean(pathwaySnapshot && pathwaySnapshot.pathways.length >= 2));

  if (!isVisible('lcoeChart')) return null;

  const studioStyle: React.CSSProperties = sectionStyle ? sectionStyle('lcoeChart') : {};
  const lcoeUnavailable = isLcoeUnavailableInFrozenSource(report);
  const hasChartData = !!(report.btmOptions && report.btmOptions.length > 0 && report.datasets) && !lcoeUnavailable;

  // COMBINES fold #4 — the folded pathway-comparison sub-block. Rendered beneath
  // the LCOE body (in BOTH the has-data and no-chart-data render paths) under a
  // labelled <h3> sub-head. PathwayComparisonReportSection's inner SectionShell
  // heading is suppressed (suppressHeading) so only its body renders here.
  const pathwayFoldSubBlock = hasPathwayFold ? (
    <div className="space-y-2 pt-5" data-subsection="pathway_comparison">
      <h3
        className="text-[11px] font-semibold uppercase"
        style={{ fontFamily: 'var(--report-body-font, inherit)', letterSpacing: '0.14em', color: roleColor(theme, 'ink') }}
      >
        Pathway Comparison
      </h3>
      <PathwayComparisonReportSection report={report} label="Pathway Comparison" suppressHeading />
    </div>
  ) : null;

  if (!hasChartData) {
    const emptyMessage = lcoeUnavailable
      ? `No site-specific LCOE comparison is available for this report because financial assumptions and capital-structure inputs were not recorded in the frozen source report (section omitted for insufficient evidence; ${formatElectricityPriceReference(report.datasets?.electricity ?? {})} Retained electricity and fuel figures in the power sections are regional context benchmarks only and cannot be compared as all-in served-load LCOE).`
      : GENERIC_SECTION_INSUFFICIENT_MESSAGE;
    return (
      <EditorialSectionShell
        id="lcoeChart"
        data-section="lcoeChart"
        data-section-id="lcoeChart"
        data-section-columns={sectionPageColumnCount ? String(sectionPageColumnCount('lcoeChart')) : undefined}
        extraClassName={reportSectionGapClass(theme.density)}
        style={{
          ...studioStyle,
          fontFamily: theme.bodyFont,
          color: roleColor(theme, 'body'),
          scrollMarginTop: '80px',
          borderBottom: `1px solid ${roleColor(theme, 'hair')}`,
        }}
        data-testid="lcoe-chart-section"
        omitSectionSidebar={omitSectionSidebar}
        shellPadding={shellPadding}
        theme={theme}
        editorialActive={editorialActive}
        sectionNumber={editorialSectionNumber}
        heading="LCOE Cost Comparison"
      >
        {/* Classic mode (flag OFF) — caller-owned head (byte-identical to the
             retired ClassicSectionShell path). Editorial mode: the shell owns the
             opener, so this is suppressed. */}
        {!editorialActive && (
          <h2
            className={`${REPORT_SECTION_H2_CLASS} mb-4`}
            style={reportSectionHeadingStyle(theme)}
            data-heading="true"
          >
            LCOE Cost Comparison
          </h2>
        )}
        <div
          className="text-xs italic px-3 py-2"
          // William QA 2026-06-28 — left accent rule removed report-wide
          style={{
            color: roleColor(theme, 'muted'),
            background: 'transparent',
          }}
          data-testid="lcoe-chart-empty"
        >
          {emptyMessage}
        </div>
        {/* COMBINES fold #4 — pathway comparison still folds in even when the
            LCOE chart itself has no data, so the pinned pathway content is never
            lost just because btmOptions/datasets are absent. */}
        {pathwayFoldSubBlock}
      </EditorialSectionShell>
    );
  }

  // PH-A2 #8: `datasets` is now optional (absent on Firestore-fallback
  // records). Guard at the parent and bail out cleanly if the metadata-
  // only path lands here — the LCOE chart is data-driven, no datasets
  // means no chart.
  const ds = report.datasets!;
  // Canonicalise the retail rate to ¢/kWh BEFORE the $/MWh conversion below.
  // A document-extracted `$0.08/kWh` tariff lands as `retailRate = 0.08`,
  // which would mis-scale the grid ReferenceLine 100× (0.8 $/MWh instead of
  // 80 $/MWh) and disagree with the (now-normalized) Electricity card. Route
  // through the SAME single conversion point the card uses.
  const gridRate = normalizeElectricityPriceToCents(ds.electricity ?? {});

  // TRACK C — preserve saved financial-model and benchmark pathway values.
  // Benchmark engine labels remain reported provenance until their cash-flow
  // dollar basis is established. Numeric captions stay visible, while a
  // comparative plot requires complete, compatible basis metadata. Missing
  // values stay pending; published reference ranges are context, not fallback
  // bars. Scenario ranges use their actual endpoints, never an artificial band.
  // The electricity reference remains contextual and is not plotted against
  // rows whose comparison basis is unverified.
  const corrected = resolveReportCorrectedLcoe(report);
  const explainerData = deriveLcoeExplainerRows(report);
  const hasUnverifiedBenchmark = explainerData.rows.some(row => !row.pending && row.basis === 'benchmark-pathway');
  const numericRows = explainerData.rows.filter(row => !row.pending && !row.industryNorm);
  const chartBasisUnavailable = numericRows.some(row => !rowHasCompleteBasis(row))
    || (numericRows.length > 1 && !assessLcoePathwayComparability(numericRows).canRankPathways);

  const ROW_DEFS: Array<{ tech: string; bucket: LcoeTechBucket; paletteIdx: number }> = [
    { tech: 'Nuclear SMR',  bucket: 'nuclear', paletteIdx: 0 },
    { tech: 'Natural Gas',  bucket: 'gas',     paletteIdx: 1 },
    { tech: 'Solar + BESS', bucket: 'solar',   paletteIdx: 2 },
    { tech: 'Wind + BESS',  bucket: 'wind',    paletteIdx: 3 },
  ];

  const rows: LcoeRow[] = ROW_DEFS.map(({ tech, bucket, paletteIdx }) => {
    const entry = correctedLcoeForBucket(corrected, bucket);
    const mid = entry.midUsdPerMwh;
    if (mid == null) {
      return {
        tech,
        low: 0,
        high: 0,
        mid: null,
        spacer: 0,
        range: 0,
        note: formatPublishedReferenceNoteForBucket(bucket),
        paletteIdx,
        pending: true,
        industryNorm: false,
      };
    }
    const low = Math.round(entry.lowUsdPerMwh ?? mid);
    const high = Math.round(entry.highUsdPerMwh ?? mid);
    const isRange = low < high;
    const noteSource =
      entry.basis === 'project-finance'
        ? 'user financial model'
        : entry.source === 'persisted'
          ? 'benchmark pathway at generation'
          : 'benchmark pathway model';
    const noteParts: string[] = [
      noteSource,
      entry.currencyYear == null ? 'currency year unknown' : entry.basis === 'benchmark-pathway' ? `reported currency year: ${entry.currencyYear}` : `${entry.currencyYear}$`,
      describeLcoeDollarBasis(entry),
    ];
    if (entry.subsidyStatus === 'subsidized') {
      noteParts.push(entry.subsidySummary ? `subsidized: ${entry.subsidySummary}` : 'subsidized');
    } else if (entry.subsidyStatus === 'unsubsidized') {
      noteParts.push('unsubsidized');
    } else {
      noteParts.push('incentive basis unknown');
    }
    if (entry.maturityScenario && entry.maturityScenario !== 'unspecified') {
      noteParts.push(entry.maturityScenario);
    }
    if (isRange && (entry.scenarioCount ?? 0) > 1) {
      noteParts.push(`${entry.scenarioCount} scenarios`);
    }
    return {
      tech,
      low,
      high,
      mid: Math.round(mid),
      spacer: isRange ? low : 0,
      range: isRange ? high - low : high,
      note: `${formatApproxLcoePerMwh(mid)} (${noteParts.join(', ')})`,
      paletteIdx,
      pending: false,
      industryNorm: false,
    };
  });

  const hasPendingReference = rows.some(r => r.pending);
  const analysisText = composeLcoeExplainer(explainerData.rows, explainerData.gridLineUsdPerMwh, explainerData.gridBasis);
  const referenceContext = composePublishedLcoeReferenceContext();
  const analysisIncludesReferenceContext = analysisText.includes(referenceContext);
  // Grid retail $/MWh = retail rate (¢/kWh) × 10. Drives the
  // ReferenceLine; not shown as a bar since it's a single point not
  // a range.
  const gridLine = gridRate != null ? Math.round(gridRate * 10 * 10) / 10 : null;
  const showGridReferenceLine = gridLine != null && (numericRows.length === 0
    || numericRows.every(row => canCompareRowToGrid(row, gridLine, explainerData.gridBasis)));

  // X-axis upper bound: max of all corrected `high` values + the grid line, with
  // a 10 $/MWh padding cap. Falls back to the grid line / a sane default when
  // every bucket is pending so the axis is never degenerate.
  const xMax = Math.max(...rows.map(r => r.high), gridLine ?? 0, 50) + 10;

  // P2.6 — theme palette via the single chart-theme adapter. Legacy
  // (no-preset) reports carry DEFAULT_THEME.chartPalette so they resolve to
  // the same effective colours as before; a preset / tenant palette reskins.
  // Indices 0-3 cover the four BTM techs; index 4 is the grid ReferenceLine.
  const palette = resolveReportChartPalette(theme, 5);

  return (
    <EditorialSectionShell
      id="lcoeChart"
      data-section="lcoeChart"
      data-section-columns={sectionPageColumnCount ? String(sectionPageColumnCount('lcoeChart')) : undefined}
      extraClassName={reportSectionGapClass(theme.density)}
      style={{
        ...studioStyle,
        fontFamily: theme.bodyFont,
        color: roleColor(theme, 'body'),
        scrollMarginTop: '80px',
        borderBottom: `1px solid ${roleColor(theme, 'hair')}`,
      }}
      data-testid="lcoe-chart-section"
      omitSectionSidebar={omitSectionSidebar}
      shellPadding={shellPadding}
      theme={theme}
      editorialActive={editorialActive}
      sectionNumber={editorialSectionNumber}
      heading="LCOE Cost Comparison"
    >
      {/* Classic mode (flag OFF) — caller-owned head (byte-identical to the
           retired ClassicSectionShell path). Editorial mode: the shell owns the
           opener, so this is suppressed. */}
      {!editorialActive && (
        <h2
          className={`${REPORT_SECTION_H2_CLASS} mb-4`}
          style={reportSectionHeadingStyle(theme)}
          data-heading="true"
        >
          LCOE Cost Comparison
        </h2>
      )}

      <div className="w-full" data-testid="lcoe-chart-canvas" data-export-capture="" data-export-id="lcoe-chart-canvas" data-export-title="Levelized Cost of Energy">
        {chartBasisUnavailable ? (
          <p className="py-4 text-sm" data-testid="lcoe-comparison-basis-pending">
            {hasUnverifiedBenchmark
              ? 'Comparative chart pending: cash-flow dollar basis unverified. '
              : 'Comparative chart pending: a common dollar, incentive and service basis is not established. '}
            Reported numeric values and source labels are retained below.
          </p>
        ) : (
        <ChartFrame
          exportId="lcoe-chart-canvas"
          traceName="report:lcoe"
          minWidth={100}
          aspect={CHART_FRAME_BOUNDS.bars.aspect}
          minHeight={CHART_FRAME_BOUNDS.bars.minHeight}
          maxHeight={CHART_FRAME_BOUNDS.bars.maxHeight}
          legibilityFloor={CHART_FRAME_BOUNDS.bars.legibilityFloor}
        >
          <BarChart layout="vertical" data={rows} margin={CHART_PLOT_MARGIN.lcoe} barCategoryGap="34%">
            {/* Grid chrome reads the theme hairline; series Cell fills +
                 ReferenceLine still come from the resolved palette below. */}
            {/* 'rule' (not 'hair', now transparent) so the chart gridlines stay visible. */}
            <CartesianGrid horizontal={false} stroke={roleColor(theme, 'rule')} strokeDasharray="2 4" />
            <XAxis
              type="number"
              domain={[0, xMax]}
              unit=" $/MWh"
              tick={{ fontSize: CHART_INK.axisTickFontSize, fill: roleColor(theme, 'muted') }}
              tickLine={false}
              label={{ value: '$/MWh', position: 'insideBottom', offset: -5, fontSize: CHART_INK.axisLabelFontSize, fill: roleColor(theme, 'muted') }}
            />
            <YAxis
              type="category"
              dataKey="tech"
              tick={{ fontSize: 12, fill: roleColor(theme, 'ink') }}
              tickLine={false}
              axisLine={{ stroke: roleColor(theme, 'rule') }}
              width={100}
            />
            <Tooltip
              cursor={{ fill: 'transparent' }}
              content={({ payload }) => {
                const entry =
                  payload?.find(p => p.dataKey === 'range') ??
                  [...(payload ?? [])].reverse().find(p => (p.payload as LcoeRow | undefined)?.tech != null);
                const r = entry?.payload as LcoeRow | undefined;
                if (!r) return null;
                return (
                  <div
                    className="px-2 py-1 text-xs"
                    style={{
                      background: roleColor(theme, 'subtleBg'),
                      border: `1px solid ${roleColor(theme, 'hair')}`,
                    }}
                  >
                    <div className="font-bold" style={{ color: roleColor(theme, 'ink') }}>{r.tech}</div>
                    <div style={{ color: roleColor(theme, 'muted') }}>{lcoeValueLabel(r)}</div>
                    {r.note && <div style={{ color: roleColor(theme, 'muted') }}>{r.note}</div>}
                  </div>
                );
              }}
            />
            {gridLine != null && showGridReferenceLine && (
              <ReferenceLine
                x={gridLine}
                stroke={palette[4] ?? '#A32D2D'}
                strokeDasharray="4 4"
                label={{
                  value: `${ds.electricity?.basis === 'state-industrial-average' ? 'EIA state benchmark' : 'Electricity price reference'} ${formatApproxLcoePerMwh(gridLine)}`,
                  position: 'top',
                  dy: -4,
                  // 11 px = 8.25 pt: the validated chart-label floor is 8 pt. The deck never
                  // measured this label (it rasterised charts first); native print does.
                  fontSize: 11,
                  fill: palette[4] ?? '#A32D2D',
                }}
              />
            )}
            {/* Stacked spacer (transparent) + range (themed). The
                 spacer pushes the visible bar's left edge to `low`. */}
            <Bar isAnimationActive={false} dataKey="spacer" stackId="lcoe" fill="transparent" />
            <Bar isAnimationActive={false} dataKey="range"  stackId="lcoe" radius={[0, 2, 2, 0]}>
              {rows.map((r, i) => (
                <Cell key={`cell-${i}`} fill={palette[r.paletteIdx % palette.length]!} />
              ))}
            </Bar>
          </BarChart>
        </ChartFrame>
        )}
      </div>

      {/* SECTION-PORT (D2 → D1 fallback, "cost stack") — the locked LCOE
           layout is a per-technology capex/fuel/O&M/carbon STACK
           (EditorialStackBar). GAP: that cost SPLIT is NOT generated — the
           report carries only a single corrected LCOE figure / band per
           technology (resolveReportCorrectedLcoe → midUsdPerMwh), so the D2
           stack DEGRADES to the documented D1 single-figure table here.
           Composed BENEATH the existing chart head (chart wiring + captions +
           provenance footnote untouched), the table reads the SAME `rows`
           already derived above — no new data, no fabricated split. */}
      {/* GAP: per-technology LCOE cost SPLIT (capex / fuel / O&M / carbon)
              is not emitted by gen — only a single corrected $/MWh midpoint
              (resolveReportCorrectedLcoe) per bucket. When gen emits the split,
              swap this D1 EditorialDataTable for the D2 EditorialStackBar
              (per-tech stacked capex/fuel/O&M/carbon). Do NOT invent the split. */}
      {/* William QA 2026-06-29 — the LCOE range CHART above is canonical; the
          duplicate "Technology / Est. LCOE / Basis" EditorialDataTable that
          re-rendered the same data (the D1 fallback) is removed to kill the
          chart-then-table content duplication. */}

      {/* LCOE-EXPLAIN-1 — the exhibit's write-up: deterministic analysis
           composed from the chart's own rows (see composeLcoeExplainer).
           Reads as body prose under the chart on every surface. */}
      {analysisText && (
        <p
          className="mt-4 leading-relaxed"
          style={{ color: roleColor(theme, 'body'), fontFamily: theme.bodyFont, fontSize: 13 }}
          data-testid="lcoe-explainer"
        >
          {analysisText}
        </p>
      )}

      {/* Per-tech adjustment captions — readable in PDF since they
           sit below the chart rather than overlaid on the bars. Chrome
           reads roleColor; the swatch fill stays the resolved palette. */}
      <div
        className="mt-3 grid grid-cols-1 sm:grid-cols-2 gap-x-6 gap-y-1 text-[10px]"
        style={{ color: roleColor(theme, 'body') }}
        data-testid="lcoe-chart-captions"
      >
        {rows.map(r => (
          <div key={r.tech} className="flex items-baseline gap-2">
            <span
              className="inline-block w-2 h-2 shrink-0 rounded-sm"
              style={{ background: palette[r.paletteIdx % palette.length] }}
              aria-hidden
            />
            <div className="min-w-0 leading-snug">
              <span className="font-bold whitespace-nowrap" style={{ color: roleColor(theme, 'ink') }}>{r.tech}:</span>{' '}
              <span className="whitespace-nowrap">{lcoeValueLabel(r)}</span>
              {r.note && (
                <span style={{ color: roleColor(theme, 'muted') }}>
                  {` · ${r.note}`}
                </span>
              )}
            </div>
          </div>
        ))}
        {hasPendingReference && (
          <ReportSourceNote
            className="col-span-full mt-1"
            testId="lcoe-industry-norm-footnote"
          >
            Pending rows have no verified site-specific LCOE (no unsupported midpoint average or graphical ±8% band is drawn). {analysisIncludesReferenceContext ? (
              <>{LCOE_REFERENCE_VERIFICATION_NOTE} See the analysis above for the reported ranges and source URLs, the row captions for their individual basis labels, and Appendix &amp; References for source details.</>
            ) : (
              <>{referenceContext} See Appendix &amp; References.</>
            )}
          </ReportSourceNote>
        )}
      </div>

      <ReportSourceNote className="mt-2" testId="lcoe-corrected-methodology-note">
        {composeLcoeMethodologyNote(explainerData.rows)}
        {formatElectricityPriceReference(ds.electricity ?? {})}
      </ReportSourceNote>
      <LcoeProvenanceFootnote report={report} />
      {/* COMBINES fold #4 — pinned pathway comparison, folded beneath the LCOE
          cost comparison under its own sub-head (feasibility only). */}
      {pathwayFoldSubBlock}
    </EditorialSectionShell>
  );
});

LcoeChartSection.displayName = 'LcoeChartSection';

export { LcoeChartSection };
